import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, updateAuthToken } from '@/lib/api';

interface User {
  id: string;
  username: string;
  role: string;
  outletId?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing token on mount
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Verify token is still valid
      fetchCurrentUser().catch(() => {
        localStorage.removeItem('auth_token');
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.auth.getCurrentUser();
      if (response.success && response.data) {
        // Handle nested data structure from API client (same pattern as useFrames, useOutlets)
        const userData = (response.data as { data?: { userId: string; username: string; role: string; outletId?: string } }).data || response.data;
        if (userData?.userId) {
          setUser({
            id: userData.userId,
            username: userData.username,
            role: userData.role,
            outletId: userData.outletId,
          });
        } else {
          throw new Error('Invalid token');
        }
      } else {
        throw new Error('Invalid token');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (username: string, password: string) => {
    try {
      const response = await api.auth.login({ username, password });
      
      if (response.success && response.data) {
        // The API client returns the server response as-is in data
        // Server returns: { success: true, data: { token, user, expiresAt } }
        const loginData = response.data as any;
        
        // Check if we have nested data structure
        const actualData = loginData.data || loginData;
        
        if (actualData.token && actualData.user) {
          updateAuthToken(actualData.token);
          setUser({
            id: actualData.user.userId,
            username: actualData.user.username,
            role: actualData.user.role,
            outletId: actualData.user.outletId,
          });
          return;
        }
      }
      
      throw new Error(response.error?.message || 'Login failed');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    updateAuthToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
